import React, { ReactNode } from "react";
import { FieldProps, getIn, useFormikContext } from "formik";
import {
  Autocomplete,
  AutocompleteChangeReason,
  Chip,
  TextField,
  TextFieldProps,
  Tooltip,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import Text from "../base/Text";
import theme from "../../theme";

interface Props {
  options: any[];
  multiple?: boolean;
  onSelected?: (
    event: React.SyntheticEvent,
    value: any | any[],
    reason?: AutocompleteChangeReason,
  ) => void;
  noOptionsText?: string;
  onFocus?: () => void;
  staticFilter?: boolean;
  renderOption?: (
    props: React.HtmlHTMLAttributes<HTMLLIElement>,
    option: any,
  ) => ReactNode;
  freeSolo?: boolean;
  clearAfterSelect?: boolean;
}

const FormikAutocomplete: React.FC<FieldProps & TextFieldProps & Props> = (
  props,
) => {
  const {
    options,
    multiple,
    form,
    helperText,
    field,
    onSelected,
    error,
    noOptionsText,
    onFocus,
    staticFilter,
    renderOption,
    freeSolo,
    clearAfterSelect,
    ...rest
  } = props;

  const isTouched = getIn(form.touched, field.name);
  const errorMessage = getIn(form.errors, field.name);

  const { setFieldValue } = useFormikContext();

  const arr = (v: any | any[]) => (Array.isArray(v) ? v : v == null ? [] : [v]);
  const keyOf = (x: any) =>
    typeof x === "string"
      ? x.toLowerCase()
      : (x?.value ?? x?.name ?? x?.id ?? String(x)).toString().toLowerCase();

  const onChange = (event: React.ChangeEvent<{}>, value: any) => {
    setFieldValue(props.field.name, value || null);
  };

  const handleSelected = (
    event: React.SyntheticEvent,
    value: any | any[],
    reason?: AutocompleteChangeReason,
  ) => {
    const current = arr(getIn(form.values, field.name));
    const incoming = arr(value).filter(
      (v) => v !== "" && v !== null && v !== undefined,
    );

    const merged = [...current, ...incoming];
    const dedup = Array.from(
      new Map(merged.map((v) => [keyOf(v), v])).values(),
    );

    setFieldValue(field.name, freeSolo && multiple ? dedup : value || null);

    onSelected?.(event, value, reason);
    if (clearAfterSelect) onSelected?.(event, null);
  };

  const defaultRenderOption = (
    props: React.HtmlHTMLAttributes<HTMLLIElement>,
    option: any,
  ) => {
    return (
      <li
        {...props}
        key={
          option?.id > 0
            ? option?.id
            : option?.value || option?.name || option?.type || option
        }
        style={{ backgroundColor: `${theme.palette.background.paper}` }}
      >
        {option?.value ||
          option?.name ||
          option?.type ||
          `${option?.firstName} ${option?.lastName}` ||
          option ||
          ""}
      </li>
    );
  };

  return (
    <Autocomplete
      multiple={!!multiple}
      key={staticFilter && !freeSolo ? JSON.stringify(options) : undefined}
      disableCloseOnSelect={!!multiple && options?.length! > 1}
      disablePortal
      freeSolo={freeSolo}
      value={
        multiple
          ? Array.isArray(field.value)
            ? field.value
            : []
          : freeSolo
          ? field.value ?? null
          : field.value ?? null
      }
      filterOptions={!!staticFilter ? undefined : (x) => x}
      filterSelectedOptions
      options={options}
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          return option;
        } else {
          return (
            option?.name ||
            option?.value ||
            option?.type ||
            `${option?.firstName} ${option?.lastName}` ||
            option?.id?.toString() ||
            ""
          );
        }
      }}
      onInputChange={(_e, value) => {
        if (!!freeSolo && !multiple) {
          setFieldValue(field.name, value);
        }
      }}
      onFocus={onFocus}
      noOptionsText={noOptionsText}
      isOptionEqualToValue={(option, value) => {
        if (typeof option === "string" && typeof value === "string") {
          return option === value;
        }
        if (typeof option === "object" && typeof value === "string") {
          return option.value === value;
        }
        if (typeof option === "string" && typeof value === "object") {
          return option === value.value;
        }
        if (option?.id !== undefined && value?.id !== undefined) {
          return option.id === value.id;
        }
        return option?.value === value?.value;
      }}
      popupIcon={<ExpandMore fontSize="medium" color="primary" />}
      onChange={onSelected ? handleSelected : onChange}
      renderOption={renderOption || defaultRenderOption}
      blurOnSelect={clearAfterSelect}
      renderValue={
        multiple
          ? (value: any[]) =>
              value.map((option, index: number) => {
                const label =
                  typeof option === "string"
                    ? option
                    : option?.name ||
                      option?.value ||
                      option?.type ||
                      `${option?.firstName} ${option?.lastName}` ||
                      option?.id?.toString() ||
                      "";
                return (
                  <Tooltip title={label}>
                    <Chip
                      variant="outlined"
                      label={label}
                      key={index}
                      onDelete={() => {
                        const newOption = [...value];
                        newOption.splice(index, 1);
                        setFieldValue(field.name, newOption);
                      }}
                    />
                  </Tooltip>
                );
              })
          : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          helperText={
            helperText ?? (isTouched && errorMessage) ? (
              <Text text={errorMessage} variant="subtitle1" component="span" />
            ) : (
              " "
            )
          }
          onKeyDown={(e) => {
            const k = e.key;
            const isDelete = k === "Backspace" || k === "Delete";
            const target = e.target as HTMLInputElement;
            const isEmpty = (target.value ?? "") === "";
            if (isDelete && multiple && isEmpty) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
          }}
          {...rest}
          error={error ?? Boolean(isTouched && errorMessage)}
        />
      )}
    />
  );
};

export default FormikAutocomplete;
